import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';

// Import components
import Header from './components/Header/Header';
import TopBar from './components/Common/TopBar';
import Hero from './components/Hero/Hero';
import About from './components/About/About';
import Stats from './components/Stats/Stats';
import Details from './components/Details/Details';
import Testimonials from './components/Testimonials/Testimonials';
import Team from './components/Team/Team';
import Pricing from './components/Pricing/Pricing';
import Faq from './components/Faq/Faq';
import Contact from './components/Contact/Contact';
import Footer from './components/Footer/Footer';
import ScrollTop from './components/Common/ScrollTop';
import WhatsAppButton from './components/Common/WhatsAppButton';
import Preloader from './components/Common/Preloader';
import DigiCloud from './components/DigiCloud/DigiCloud';
import Busy from './components/Busy/Busy';
import SharePro from './components/SharePro/SharePro';
import TallyPrime from './components/TallyPrime/TallyPrime';
import TSS from './components/TallyServicesSoftware/TSS';
import TallyPrimeAWS from './components/TallyPrimeAWS/TallyPrimeAWS';

// Blog components
import BlogList from './components/Blog/BlogList';
import BlogPost from './components/Blog/BlogPost';
import AdminLogin from './components/Admin/AdminLogin';
import BlogManager from './components/Admin/BlogManager';
import ProtectedRoute from './components/Admin/ProtectedRoute';

// Location listener component to handle route changes
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

function App() {
  const [loading, setLoading] = useState(true);
  const [showTopBar, setShowTopBar] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [prevScrollPos, setPrevScrollPos] = useState(window.pageYOffset);
  const [headerHidden, setHeaderHidden] = useState(false);

  // Preload images
  useEffect(() => {
    const preloadImages = () => {
      const heroBgPath = `${process.env.PUBLIC_URL}/assets/img/hero-bg-2.jpg`;
      const testimonialsBgPath = `${process.env.PUBLIC_URL}/assets/img/testimonials-bg.jpg`;
      
      const img1 = new Image();
      const img2 = new Image();
      
      img1.src = heroBgPath;
      img2.src = testimonialsBgPath;
    };
    
    preloadImages();
  }, []);

  // Preloader effect
  useEffect(() => {
    // Show preloader for at least 2 seconds to ensure assets load
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Scroll handling effect
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.pageYOffset;
      
      // Check if scrolled down from the very top
      setScrolled(currentScrollPos > 50);
      
      // Hide header when scrolling down, show when scrolling up
      setHeaderHidden(prevScrollPos < currentScrollPos && currentScrollPos > 150);
      
      // Hide top bar after scrolling down 300px
      setShowTopBar(currentScrollPos <= 300);
      
      // Update previous scroll position
      setPrevScrollPos(currentScrollPos);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [prevScrollPos]);

  // Main content (Home page)
  const HomeContent = () => (
    <main>
      <Hero />
      <About />
      <Stats />
      <Details />
      <Testimonials />
      <Team />
      <Pricing />
      <Faq />
      <Contact />
    </main>
  );

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <div className="App">
        {loading ? (
          <Preloader />
        ) : (
          <>
            <ScrollToTop />
            {/* Use our custom TopBar component */}
            {showTopBar && <TopBar />}
            <Header 
              showTopBar={false} // We're handling the top bar separately now
              scrolled={scrolled}
              headerHidden={headerHidden}
            />
            
            <Routes>
                {/* Main Routes */}
                <Route path="/" element={<HomeContent />} />
                
                {/* Service Routes - Now Public */}
                <Route path="/digicloud" element={<DigiCloud />} />
                <Route path="/busy" element={<Busy />} />
                <Route path="/sharepro" element={<SharePro />} />
                <Route path="/tallyprime" element={<TallyPrime />} />
                <Route path="/tallyprime-on-aws" element={<TallyPrimeAWS />} />
                <Route path="/tally-software-service" element={<TSS />} />
                
                {/* Blog Routes */}
                <Route path="/blog" element={<BlogList />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                
                {/* Admin Routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/*" element={
                  <ProtectedRoute>
                    <BlogManager />
                  </ProtectedRoute>
                } />
                
                <Route path="*" element={<Navigate to="/" />} />
            </Routes>
            
            <Footer />
            <ScrollTop />
            <WhatsAppButton />
          </>
        )}
      </div>
    </Router>
  );
}

export default App;
